import React, { useEffect, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Box, Stack, Tabs, Tab, Typography } from "@mui/material";
import TaskAnalytics from "../Task/TaskAnalytics";
import ApprovalAnalytics from "../Approval/ApprovalAnalytics";
import PerformanceSummaryBar from "./PerformanceSummaryBar";
import DateRangePicker from "../../task/Filter/DateRangePicker";
import {
  handleGetTaskAnalytics,
  handleGetApprovalAnalytics,
} from "../../../appStore/actions/ArkchatGroup/action";
import useAppSelectors from "../../../customHooks/useAppSelectors";
import { useAbortController } from "../../../customHooks/useAbortController";

const classes = {
  analyticsParentBox: {
    overflowX: "hidden",
    overflowY: "auto",
    height: "calc(100vh - 10rem)",
    paddingLeft: "0.5rem",
    paddingRight: "0.2rem",
    "&::-webkit-scrollbar": { width: "1px" },
    "&::-webkit-scrollbar-track": { background: "#F9F8F8" },
    "&::-webkit-scrollbar-thumb": { background: "grey" },
  },
  // Grouped tab strip — tabs share borders so they look connected
  tabGroup: {
    alignItems: "center",
    minHeight: "36px",
    "& .MuiTabs-flexContainer": { gap: 0 },
    "& .MuiTab-root": {
      minWidth: "unset",
      minHeight: "36px",
      maxHeight: "36px",
      color: "#183243",
      fontWeight: 500,
      fontSize: "0.8rem",
      textTransform: "none",
      lineHeight: "normal",
      padding: "0 14px",
      backgroundColor: "#fff",
      border: "1px solid #000",
      borderRadius: 0,
      marginLeft: "-1px", // collapse shared borders
      "&:first-of-type": {
        borderRadius: "4px 0 0 4px",
        marginLeft: 0,
      },
      "&:last-of-type": {
        borderRadius: "0 4px 4px 0",
      },
    },
    "& .MuiTab-root.Mui-selected": {
      backgroundColor: "#183243",
      color: "#fff",
      border: "1px solid #183243",
      zIndex: 1,
    },
  },
  sectionHeader: {
    display: "inline-block",
    fontWeight: 700,
    fontSize: "0.85rem",
    color: "#fff",
    backgroundColor: "#183243",
    padding: "0.4rem 1rem",
    borderRadius: "20px",
    marginBottom: "0.5rem",
  },
  filterDivider: {
    width: "1px",
    alignSelf: "stretch",
    minHeight: "36px",
    backgroundColor: "#D9D9D9",
    mx: "0.5rem",
  },
};

const PerformanceTab = ({ currentHomeIndexTab, isSideBarOpen, isTopicOpen }) => {
  const dispatch = useDispatch();
  const { currentUserData, selectedGroup, selectedTopic, selectedSubtopic } = useAppSelectors();
  const taskSummary = useSelector((state) => state?.arkchatgroup?.taskAnalyticsData?.summary);
  const approvalSummary = useSelector((state) => state?.arkchatgroup?.approvalAnalyticsData?.summary);

  const [tabValue, setTabValue] = useState(0);
  const [dateFilter, setDateFilter] = useState(null); // null | {range:"month"|"year"} | {range:"custom", from, to}
  const [isAnalyticsLoading, setIsAnalyticsLoading] = useState(false);
  const [dateAnchorEl, setDateAnchorEl] = useState(null);
  const [isDatePickerOpen, setDatePickerOpen] = useState(false);

  const analyticsAbort = useAbortController();
  const latestRequestId = useRef(0);

  const canShowPerformance = selectedGroup?.isGroup || selectedGroup?.isAllGroups;
  // When a topic or subtopic is selected, the summary bar and section labels
  // are hidden — only the analytics cards are shown.
  const isTopicView = !!(selectedTopic?.id || selectedSubtopic?.id);

  // Selecting the pinned "All Groups" sidebar entry omits groupId entirely —
  // the backend already aggregates across every group the caller belongs to
  // when groupId is absent, so this reuses that behavior rather than needing
  // a separate "combined" endpoint.
  const buildQuery = ({ userId, archived, dateFilter: df } = {}) => {
    const parts = [];
    if (!selectedGroup?.isAllGroups) {
      parts.push(`groupId=${selectedGroup?.id}`);
      if (selectedSubtopic?.id) {
        parts.push(`subtopicId=${selectedSubtopic.id}`);
      } else if (selectedTopic?.id) {
        parts.push(`topicId=${selectedTopic.id}`);
      }
    }
    if (archived) {
      parts.push("is_archived=true");
    }
    if (userId) {
      parts.push(`userId=${userId}`);
    }
    if (df?.range === "custom" && df?.from && df?.to) {
      parts.push(`range=custom&due_custom_from=${encodeURIComponent(df.from)}&due_custom_to=${encodeURIComponent(df.to)}`);
    } else if (df?.range === "month" || df?.range === "year") {
      parts.push(`range=${df.range}`);
    }
    return parts.join("&");
  };

  const fetchAnalytics = (params) => {
    const signal = analyticsAbort.getSignal();
    const requestId = ++latestRequestId.current;
    setIsAnalyticsLoading(true);
    const queryParam = buildQuery(params);
    Promise.all([
      dispatch(handleGetTaskAnalytics(queryParam, signal)),
      dispatch(handleGetApprovalAnalytics(queryParam, signal)),
    ]).finally(() => {
      // Guards against an in-flight request that got superseded (group/filter
      // switched again before it returned) clearing the loading state late.
      if (latestRequestId.current === requestId) {
        setIsAnalyticsLoading(false);
      }
    });
  };

  useEffect(() => {
    if (canShowPerformance) {
      setTabValue(0);
      fetchAnalytics({ dateFilter });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedGroup?.id, selectedGroup?.isGroup, selectedGroup?.isAllGroups, selectedTopic?.id, selectedSubtopic?.id]);

  const handleTabChange = (_, newValue) => {
    setTabValue(newValue);
    if (!canShowPerformance) return;
    fetchAnalytics({
      userId: newValue === 1 ? currentUserData?.id : undefined,
      archived: newValue === 3,
      dateFilter,
    });
  };

  const handleDateTabChange = (event, newRange) => {
    if (newRange === "custom") {
      setDateAnchorEl(event.currentTarget);
      setDatePickerOpen(true);
      return;
    }
    const nextFilter = newRange === "none" ? null : { range: newRange };
    setDateFilter(nextFilter);
    if (!canShowPerformance) return;
    fetchAnalytics({
      userId: tabValue === 1 ? currentUserData?.id : undefined,
      archived: tabValue === 3,
      dateFilter: nextFilter,
    });
  };

  const handleCustomRangeApply = (range) => {
    setDatePickerOpen(false);
    const nextFilter = range ? { range: "custom", from: range.startDate, to: range.endDate } : null;
    setDateFilter(nextFilter);
    if (!canShowPerformance) return;
    fetchAnalytics({
      userId: tabValue === 1 ? currentUserData?.id : undefined,
      archived: tabValue === 3,
      dateFilter: nextFilter,
    });
  };

  return (
    <Box width="100%">
      <Stack direction="row" width="100%">
        <Box direction="column" gap="0.2rem" width="100%">
          <Box
            direction="row"
            display="flex"
            gap="0.5rem"
            alignItems="center"
            width="100%"
            flexWrap="wrap"
            justifyContent="flex-end"
            pt="0.5rem"
          >
            <Tabs
              value={tabValue}
              onChange={handleTabChange}
              TabIndicatorProps={{ style: { backgroundColor: "transparent" } }}
              sx={classes.tabGroup}
            >
              <Tab disableRipple label="All" />
              <Tab disableRipple label="Mine" />
              <Tab disableRipple label="Team" />
              <Tab disableRipple label="Archive" />
            </Tabs>

            <Box sx={classes.filterDivider} />

            <Tabs
              value={dateFilter?.range || "none"}
              onChange={handleDateTabChange}
              TabIndicatorProps={{ style: { backgroundColor: "transparent" } }}
              sx={classes.tabGroup}
            >
              <Tab disableRipple value="none" label="All" />
              <Tab disableRipple value="month" label="Month" />
              <Tab disableRipple value="year" label="Year" />
              <Tab disableRipple value="custom" label="Custom" />
            </Tabs>
            <DateRangePicker
              open={isDatePickerOpen}
              anchorEl={dateAnchorEl}
              onClose={() => setDatePickerOpen(false)}
              onApply={handleCustomRangeApply}
              selectedDateRange={dateFilter?.range === "custom" ? { startDate: dateFilter.from, endDate: dateFilter.to } : null}
              setSelectedDateRange={() => { }}
            />
          </Box>

          <Box
            sx={{
              ...classes.analyticsParentBox,
              opacity: isAnalyticsLoading ? 0.5 : 1,
              transition: "opacity 0.15s ease",
            }}
          >
            {canShowPerformance ? (
              <>
                {/* Summary bar — hidden when a topic is selected */}
                {!isTopicView && (
                  <Box mt="0.4rem" mb="0.4rem">
                    <PerformanceSummaryBar taskSummary={taskSummary} approvalSummary={approvalSummary} />
                  </Box>
                )}

                <Box mb="0.4rem">
                  {!isTopicView && (
                    <Typography sx={classes.sectionHeader}>Task Performance</Typography>
                  )}
                  <TaskAnalytics
                    taskTabValue={tabValue}
                    selectedTaskGroup={selectedGroup}
                    selectedTaskmember={[]}
                    isSideBarOpen={isSideBarOpen}
                    isTopicOpen={isTopicOpen}
                  />
                </Box>

                {!isTopicView && (
                  <Box>
                    <Typography sx={classes.sectionHeader}>Approval Performance</Typography>
                    <ApprovalAnalytics
                      isSideBarOpen={isSideBarOpen}
                      isTopicOpen={isTopicOpen}
                    />
                  </Box>
                )}
              </>
            ) : (
              <Box display="flex" justifyContent="center" alignItems="center" height="100%">
                <Typography fontSize="0.9rem" color="#888">
                  Select a group to view its performance
                </Typography>
              </Box>
            )}
          </Box>
        </Box>
      </Stack>
    </Box>
  );
};

export default PerformanceTab;
